import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Typography,
  Chip,
  Stack,
  Box,
  Collapse,
  IconButton,
  LinearProgress,
  Divider,
  alpha,
  tableCellClasses,
  TableSortLabel,
  Dialog,
  DialogContent,
  Fade,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  EmojiEvents as TrophyIcon,
  MilitaryTech as MedalIcon,
  WorkspacePremium as AwardIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';

/**
 * RankingTableRow - Fila individual con detalles expandibles
 */
function RankingTableRow({ entry, isCurrentUser }) {
  const { t, i18n } = useTranslation(['common']);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRankModalOpen, setIsRankModalOpen] = useState(false);

  const {
    position,
    user,
    total_points: totalPoints,
    monthly_base_score: monthlyBaseScore,
    mythological_rank_info: mythologicalRankInfo,
  } = entry;

  const rankColor = '#C9CDD2';

  // Obtener nombre y descripción según el idioma actual
  const getRankName = () => {
    if (!mythologicalRankInfo) return t('ranking.noRank', { ns: 'common', defaultValue: 'Sin rango' });
    return i18n.language === 'en' ? mythologicalRankInfo.name_en : mythologicalRankInfo.name;
  };

  const getRankDescription = () => {
    if (!mythologicalRankInfo) return '';
    return i18n.language === 'en' ? mythologicalRankInfo.description_en : mythologicalRankInfo.description;
  };

  // Obtener URL de la imagen del rango según género
  const getRankImage = () => {
    if (!entry.mythological_rank) return null;
    const rankName = entry.mythological_rank.toLowerCase();
    const genderSuffix = user.gender === 'FEMALE' ? '_M' : '';
    return `/images/ranks/${rankName}${genderSuffix}.png`;
  };

  // Iconos y colores para Top 3
  const getPositionStyle = (pos) => {
    if (pos === 1) {
      return {
        color: '#F9BD4E',
        icon: <TrophyIcon sx={{ fontSize: '1.5rem', color: '#F9BD4E' }} />,
      };
    }
    if (pos === 2) {
      return {
        color: '#d1d5db',
        icon: <MedalIcon sx={{ fontSize: '1.5rem', color: '#d1d5db' }} />,
      };
    }
    if (pos === 3) {
      return {
        color: '#9ca3af',
        icon: <AwardIcon sx={{ fontSize: '1.5rem', color: '#9ca3af' }} />,
      };
    }
    return { color: '#9aa0a6', icon: null };
  };

  const posStyle = getPositionStyle(position);

  // Generar iniciales del nombre
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <>
      <TableRow
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{
          cursor: 'pointer',
          bgcolor: isCurrentUser ? alpha('#F9BD4E', 0.08) : 'transparent',
          '&:hover': {
            bgcolor: isCurrentUser ? alpha('#F9BD4E', 0.12) : alpha('#3f4448', 0.05),
          },
          transition: 'background-color 0.2s ease',
          ...(isCurrentUser && {
            boxShadow: `inset 3px 0 0 #F9BD4E`,
          }),
        }}
      >
        {/* Posición */}
        <TableCell sx={{ width: { xs: 60, sm: 80 }, px: { xs: 0.5, sm: 2 } }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {posStyle.icon}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 900,
                color: posStyle.color,
                fontFamily: 'monospace',
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}
            >
              {position}
            </Typography>
          </Stack>
        </TableCell>

        {/* Avatar - Solo visible en pantallas medianas y grandes */}
        <TableCell sx={{ width: 80, display: { xs: 'none', md: 'table-cell' } }}>
          <Avatar
            src={user.profile_photo_path || undefined}
            alt={user.name}
            sx={{
              width: 48,
              height: 48,
              border: `2px solid ${rankColor}`,
              boxShadow: `0 0 8px ${alpha(rankColor, 0.4)}`,
              bgcolor: alpha(rankColor, 0.2),
              color: rankColor,
              fontWeight: 700,
              fontSize: '1rem',
            }}
          >
            {!user.profile_photo_path && getInitials(user.name)}
          </Avatar>
        </TableCell>

        {/* Nombre - En móvil incluye el avatar */}
        <TableCell sx={{ px: { xs: 1, sm: 2 } }}>
          <Stack direction="row" alignItems="center" spacing={{ xs: 1, sm: 1 }}>
            {/* Avatar en móvil (solo visible en xs y sm) */}
            <Avatar
              src={user.profile_photo_path || undefined}
              alt={user.name}
              sx={{
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 },
                border: `2px solid ${rankColor}`,
                boxShadow: `0 0 8px ${alpha(rankColor, 0.4)}`,
                bgcolor: alpha(rankColor, 0.2),
                color: rankColor,
                fontWeight: 700,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                display: { xs: 'flex', md: 'none' },
              }}
            >
              {!user.profile_photo_path && getInitials(user.name)}
            </Avatar>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    color: 'white',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: { xs: 'nowrap', sm: 'normal' },
                  }}
                >
                  {user.name}
                </Typography>
                {isCurrentUser && (
                  <Chip
                    label={t('ranking.you', { ns: 'common', defaultValue: 'TÚ' })}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.6rem',
                      fontWeight: 800,
                      bgcolor: '#F9BD4E',
                      color: 'white',
                    }}
                  />
                )}
              </Stack>
              {/* Mostrar rango debajo del nombre solo en móvil */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{
                  display: { xs: 'flex', sm: 'none' },
                }}
              >
                {getRankImage() && (
                  <Box
                    component="img"
                    src={getRankImage()}
                    alt={getRankName()}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsRankModalOpen(true);
                    }}
                    sx={{
                      width: 28,
                      height: 28,
                      objectFit: 'cover',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.15)',
                        boxShadow: `0 0 12px ${alpha(rankColor, 0.6)}`,
                      },
                    }}
                  />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: rankColor,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getRankName()}
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </TableCell>

        {/* Rango - Solo visible en pantallas medianas y grandes */}
        <TableCell sx={{ minWidth: 180, display: { xs: 'none', sm: 'table-cell' } }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              onClick={(e) => {
                e.stopPropagation();
                if (getRankImage()) setIsRankModalOpen(true);
              }}
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: alpha(rankColor, 0.15),
                border: `2px solid ${rankColor}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0.5,
                cursor: getRankImage() ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
                '&:hover': getRankImage() ? {
                  transform: 'scale(1.08)',
                  boxShadow: `0 0 20px ${alpha(rankColor, 0.5)}`,
                  bgcolor: alpha(rankColor, 0.25),
                } : {},
              }}
            >
              {getRankImage() ? (
                <Box
                  component="img"
                  src={getRankImage()}
                  alt={getRankName()}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '50%',
                  }}
                />
              ) : (
                <Box sx={{ fontSize: '1.2rem' }}>⚡</Box>
              )}
            </Box>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: rankColor,
                  lineHeight: 1.2,
                }}
              >
                {getRankName()}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: '#6b7177',
                  fontSize: '0.65rem',
                }}
              >
                {getRankDescription()}
              </Typography>
            </Box>
          </Stack>
        </TableCell>

        {/* Puntos */}
        <TableCell align="right" sx={{ minWidth: { xs: 80, sm: 140 }, px: { xs: 0.5, sm: 2 } }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={{ xs: 0.5, sm: 1 }}
            sx={{
              justifyContent: 'flex-end',
              bgcolor: alpha('#000', 0.3),
              px: { xs: 0.75, sm: 1.5 },
              py: { xs: 0.25, sm: 0.5 },
              borderRadius: 1.5,
              display: 'inline-flex',
              border: `1px solid ${alpha('#fff', 0.08)}`,
            }}
          >
            <Stack alignItems="flex-end" spacing={0}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: 'white',
                  fontFamily: 'monospace',
                  fontSize: { xs: '0.875rem', sm: '1.25rem' },
                  lineHeight: 1.2,
                }}
              >
                {(totalPoints ?? 0).toFixed(1)}
              </Typography>
            </Stack>
            <Avatar
              sx={{
                width: { xs: 16, sm: 20 },
                height: { xs: 16, sm: 20 },
                bgcolor: '#F9BD4E',
                fontSize: { xs: '0.5rem', sm: '0.65rem' },
                fontWeight: 900,
                color: '#000',
              }}
            >
              ₱
            </Avatar>
          </Stack>
        </TableCell>

        {/* Expandir */}
        <TableCell align="center" sx={{ width: { xs: 40, sm: 60 }, px: { xs: 0.5, sm: 2 } }}>
          <IconButton
            size="small"
            sx={{
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease',
              color: '#9aa0a6',
              padding: { xs: 0.5, sm: 1 },
            }}
          >
            <ExpandMoreIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
          </IconButton>
        </TableCell>
      </TableRow>

      {/* Fila Expandible */}
      <TableRow>
        <TableCell
          colSpan={6}
          sx={{
            p: 0,
            borderBottom: isExpanded ? `1px solid ${alpha('#3f4448', 0.3)}` : 'none',
          }}
        >
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box
              sx={{
                // Gris oscuro suave (fijo) para que el texto BLANCO de dentro resalte en
                // ambos modos (antes era alpha-negro: sobre card blanca quedaba gris medio).
                bgcolor: '#3A3F44',
                p: { xs: 2, sm: 3 },
                borderTop: `2px solid ${alpha(rankColor, 0.3)}`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <BarChartIcon sx={{ color: '#F9BD4E', fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#F9BD4E',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  Score OTRIXVERSE
                </Typography>
              </Stack>

              <Stack spacing={1.5}>
                {/* Este mes con barra */}
                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                    <Typography variant="body2" sx={{ color: '#FFFFFF', fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                      Este mes
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 700, fontFamily: 'monospace', fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                      {(monthlyBaseScore ?? 0).toFixed(1)} / 120 pts
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(((monthlyBaseScore ?? 0) / 120) * 100, 100)}
                    sx={{
                      height: 6,
                      borderRadius: 1,
                      bgcolor: alpha('#F9BD4E', 0.15),
                      '& .MuiLinearProgress-bar': { bgcolor: '#F9BD4E', borderRadius: 1 },
                    }}
                  />
                </Box>

                <Divider sx={{ borderColor: alpha('#3f4448', 0.3) }} />

                {/* Acumulado del año (define el ranking) */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    Acumulado del año
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 800, fontFamily: 'monospace', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    {(totalPoints ?? 0).toFixed(1)} pts
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Modal para ver imagen del rango en grande */}
      <Dialog
        open={isRankModalOpen}
        onClose={() => setIsRankModalOpen(false)}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 300 }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
          },
        }}
        BackdropProps={{
          sx: {
            bgcolor: alpha('#000', 0.85),
            backdropFilter: 'blur(8px)',
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {/* Imagen grande del rango */}
          <Box
            sx={{
              position: 'relative',
              width: { xs: 250, sm: 300 },
              height: { xs: 250, sm: 300 },
              borderRadius: '50%',
              bgcolor: alpha(rankColor, 0.15),
              border: `3px solid ${rankColor}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 0 40px ${alpha(rankColor, 0.6)}, 0 0 80px ${alpha(rankColor, 0.3)}`,
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': {
                  boxShadow: `0 0 40px ${alpha(rankColor, 0.6)}, 0 0 80px ${alpha(rankColor, 0.3)}`,
                },
                '50%': {
                  boxShadow: `0 0 60px ${alpha(rankColor, 0.8)}, 0 0 120px ${alpha(rankColor, 0.5)}`,
                },
              },
            }}
          >
            <Box
              component="img"
              src={getRankImage()}
              alt={getRankName()}
              sx={{
                width: '90%',
                height: '90%',
                objectFit: 'cover',
                borderRadius: '50%',
              }}
            />
          </Box>

          {/* Información del rango */}
          <Box
            sx={{
              textAlign: 'center',
              bgcolor: alpha('#1F2327', 0.9),
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              p: 3,
              border: `1px solid ${alpha(rankColor, 0.3)}`,
              minWidth: { xs: 280, sm: 350 },
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                color: rankColor,
                mb: 1,
                textShadow: `0 0 20px ${alpha(rankColor, 0.5)}`,
              }}
            >
              {getRankName()}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#9aa0a6',
                lineHeight: 1.6,
              }}
            >
              {getRankDescription()}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

RankingTableRow.propTypes = {
  entry: PropTypes.shape({
    position: PropTypes.number.isRequired,
    user: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      profile_photo_path: PropTypes.string,
      gender: PropTypes.oneOf(['MALE', 'FEMALE', 'OTHER']),
    }).isRequired,
    total_points: PropTypes.number.isRequired,
    monthly_base_score: PropTypes.number,
    mythological_rank: PropTypes.string,
    mythological_rank_info: PropTypes.shape({
      name: PropTypes.string,
      name_en: PropTypes.string,
      color: PropTypes.string,
      icon: PropTypes.string,
      description: PropTypes.string,
      description_en: PropTypes.string,
    }),
  }).isRequired,
  isCurrentUser: PropTypes.bool.isRequired,
};

/**
 * RankingTable - Tabla principal del ranking con sorting
 */
function RankingTable({ entries, currentUserId = null }) {
  const { t } = useTranslation(['common']);
  const [orderBy, setOrderBy] = useState('position');
  const [order, setOrder] = useState('asc');

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Ordenar entradas
  const sortedEntries = React.useMemo(() => {
    const comparator = (a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      if (orderBy === 'total_points') {
        aVal = a.total_points;
        bVal = b.total_points;
      }

      if (order === 'asc') {
        return aVal < bVal ? -1 : 1;
      }
      return aVal > bVal ? -1 : 1;
    };

    return [...entries].sort(comparator);
  }, [entries, order, orderBy]);

  return (
    <TableContainer
      component={Paper}
      sx={{
        bgcolor: 'transparent',
        backgroundImage: 'none',
        boxShadow: 'none',
      }}
    >
      <Table
        sx={{
          [`& .${tableCellClasses.root}`]: {
            borderBottom: `1px solid ${alpha('#3f4448', 0.15)}`,
            py: 1.5,
          },
        }}
      >
        <TableHead>
          <TableRow
            sx={{
              bgcolor: alpha('#1F2327', 0.6),
            }}
          >
            {/* Posición - Con Sorting */}
            <TableCell sx={{ px: { xs: 0.5, sm: 2 } }}>
              <TableSortLabel
                active={orderBy === 'position'}
                direction={orderBy === 'position' ? order : 'asc'}
                onClick={() => handleSort('position')}
                sx={{
                  color: '#F9BD4E !important',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  letterSpacing: 1,
                  '& .MuiTableSortLabel-icon': {
                    color: '#F9BD4E !important',
                  },
                }}
              >
                #
              </TableSortLabel>
            </TableCell>

            {/* Foto - Solo visible en pantallas medianas y grandes */}
            <TableCell
              sx={{
                color: '#F9BD4E',
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                letterSpacing: 1,
                display: { xs: 'none', md: 'table-cell' },
              }}
            >
              {t('ranking.photo', { ns: 'common', defaultValue: 'Foto' })}
            </TableCell>

            {/* Nombre */}
            <TableCell
              sx={{
                color: '#F9BD4E',
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                letterSpacing: 1,
                px: { xs: 1, sm: 2 },
              }}
            >
              {t('ranking.name', { ns: 'common', defaultValue: 'Nombre' })}
            </TableCell>

            {/* Rango - Solo visible en pantallas medianas y grandes */}
            <TableCell
              sx={{
                color: '#F9BD4E',
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                letterSpacing: 1,
                display: { xs: 'none', sm: 'table-cell' },
              }}
            >
              {t('ranking.rank', { ns: 'common', defaultValue: 'Rango' })}
            </TableCell>

            {/* Puntos - Con Sorting */}
            <TableCell align="right" sx={{ px: { xs: 0.5, sm: 2 } }}>
              <TableSortLabel
                active={orderBy === 'total_points'}
                direction={orderBy === 'total_points' ? order : 'asc'}
                onClick={() => handleSort('total_points')}
                sx={{
                  color: '#F9BD4E !important',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  letterSpacing: 1,
                  '& .MuiTableSortLabel-icon': {
                    color: '#F9BD4E !important',
                  },
                  flexDirection: 'row-reverse',
                }}
              >
                {t('ranking.points', { ns: 'common', defaultValue: 'Puntos' })}
              </TableSortLabel>
            </TableCell>

            {/* Detalles */}
            <TableCell
              align="center"
              sx={{
                color: '#F9BD4E',
                fontWeight: 700,
                textTransform: 'uppercase',
                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                letterSpacing: 1,
                px: { xs: 0.5, sm: 2 },
              }}
            >
              {/* En móvil, solo mostrar icono */}
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                {t('ranking.details', { ns: 'common', defaultValue: 'Detalles' })}
              </Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                •
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedEntries.map((entry) => (
            <RankingTableRow
              key={entry.user.id}
              entry={entry}
              isCurrentUser={String(currentUserId) === String(entry.user.id)}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

RankingTable.propTypes = {
  entries: PropTypes.arrayOf(
    PropTypes.shape({
      position: PropTypes.number.isRequired,
      user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string.isRequired,
        profile_photo_path: PropTypes.string,
      }).isRequired,
      total_points: PropTypes.number.isRequired,
      monthly_base_score: PropTypes.number,
    })
  ).isRequired,
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default RankingTable;
